import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check, Send, X } from "lucide-react"
import {
  UNDO_SEND_EVENT,
  cancelPendingSend,
  explainSendError,
  fetchSendStatus,
  type UndoSendDetail,
} from "@/lib/undoSend"
import { useVerschiebbar } from "@/hooks/useVerschiebbar"

/**
 * Globaler Undo-Toast fuer Soft-Send (Phase 6.4, 2026-05-08).
 *
 * Lauscht auf `portal:undo-send` CustomEvents (gefeuert von services/undoSend
 * aus den Send-Forms). Zeigt eine Toast-Karte oben rechts mit Countdown +
 * Stoppen-Button. Bei Klick: DELETE /api/v1/sends/{id} → Send wird abgebrochen.
 *
 * Position: einmal in App.tsx mounten.
 *
 * 06.08.2026: Der Toast endete frueher bei "Wird gesendet..." und verschwand.
 * Scheiterte der SMTP-Versand danach, erfuhr der Nutzer nie davon. Jetzt
 * fragt der Toast nach Ablauf des Countdowns den Endstatus ab und bleibt bei
 * einem Fehler rot stehen, bis man ihn wegklickt. Wer die Seite in der
 * Zwischenzeit verlaesst, sieht den Fehler ueber <FailedSendAlert>.
 */

type Phase =
  | "counting"    // Undo-Fenster laeuft
  | "checking"    // Countdown vorbei, Endstatus wird abgefragt
  | "sent"
  | "failed"
  | "cancelled"
  | "unknown"     // Worker noch nicht fertig — Fehler wuerde spaeter im Banner auftauchen

interface ToastState extends UndoSendDetail {
  remainingS: number
  phase: Phase
  error?: string | null
}

// Der Worker pickt faellige Sends alle 5s. 8 Versuche à 3s decken auch einen
// zaehen SMTP-Handshake ab, ohne den Toast minutenlang stehen zu lassen.
const POLL_TRIES = 8
const POLL_INTERVAL_MS = 3000

/** Diese Zustaende blenden sich selbst aus. 'failed' bleibt bewusst stehen. */
const SELBST_AUSBLENDEN: Phase[] = ["sent", "cancelled", "unknown"]

/** Wie viel vom Hinweis sichtbar bleiben muss (Breite bzw. Höhe in px). */
const SICHTBAR_X = 340
const SICHTBAR_Y = 80

/** Der Hinweis sitzt oben rechts: nach links und unten frei, nach rechts und
 *  oben nicht über die Ausgangslage hinaus. Ein roter „NICHT gesendet" bleibt
 *  stehen, bis man ihn schliesst — er darf nicht aus dem Fenster rutschen. */
function imFenster(v: { x: number; y: number }) {
  const maxLinks = Math.max(0, window.innerWidth - SICHTBAR_X - 16)
  const maxUnten = Math.max(0, window.innerHeight - SICHTBAR_Y - 16)
  return {
    x: Math.max(-maxLinks, Math.min(0, v.x)),
    y: Math.max(0, Math.min(maxUnten, v.y)),
  }
}

export function UndoSendToast() {
  const [toasts, setToasts] = useState<ToastState[]>([])
  const gepollt = useRef<Set<number>>(new Set())
  // #17 (Marlene, 20.10.2026): verdeckte die Ansicht und liess sich nicht
  // wegschieben. Derselbe Griff wie bei den Dialogen (#47). Neu mittig — hier:
  // zurück oben rechts — erst, wenn alle Hinweise weg sind.
  const { griff, stil } = useVerschiebbar(toasts.length > 0, imFenster)

  const setzePhase = (pendingId: number, phase: Phase, error?: string | null) =>
    setToasts((prev) =>
      prev.map((t) => (t.pendingId === pendingId ? { ...t, phase, error } : t)),
    )

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<UndoSendDetail>).detail
      if (!detail || !detail.pendingId) return
      const due = new Date(detail.scheduledAt).getTime()
      const remaining = Math.max(0, Math.ceil((due - Date.now()) / 1000))
      setToasts((prev) => [
        ...prev,
        {
          ...detail,
          remainingS: remaining,
          phase: remaining > 0 ? "counting" : "checking",
        },
      ])
    }
    window.addEventListener(UNDO_SEND_EVENT, handler)
    return () => window.removeEventListener(UNDO_SEND_EVENT, handler)
  }, [])

  // Countdown-Tick alle 500ms (bisschen gluecklicher als 1s)
  useEffect(() => {
    if (toasts.length === 0) return
    const tick = setInterval(() => {
      setToasts((prev) =>
        prev.map((t) => {
          if (t.phase !== "counting") return t
          const due = new Date(t.scheduledAt).getTime()
          const remaining = Math.max(0, Math.ceil((due - Date.now()) / 1000))
          return {
            ...t,
            remainingS: remaining,
            phase: remaining <= 0 ? "checking" : "counting",
          }
        }),
      )
    }, 500)
    return () => clearInterval(tick)
  }, [toasts.length])

  // Endstatus abfragen sobald das Undo-Fenster durch ist.
  useEffect(() => {
    for (const t of toasts) {
      if (t.phase !== "checking" || gepollt.current.has(t.pendingId)) continue
      gepollt.current.add(t.pendingId)
      const id = t.pendingId
      void (async () => {
        for (let i = 0; i < POLL_TRIES; i++) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
          const res = await fetchSendStatus(id)
          if (!res) continue
          if (res.status === "sent") return setzePhase(id, "sent")
          if (res.status === "failed") return setzePhase(id, "failed", res.error_message)
          if (res.status === "cancelled") return setzePhase(id, "cancelled")
        }
        setzePhase(id, "unknown")
      })()
    }
  }, [toasts])

  // Erledigte Toasts nach 4s entfernen — Fehler bleiben stehen.
  useEffect(() => {
    const removers = toasts
      .filter((t) => SELBST_AUSBLENDEN.includes(t.phase))
      .map((t) =>
        setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.pendingId !== t.pendingId))
        }, 4000),
      )
    return () => removers.forEach(clearTimeout)
  }, [toasts])

  const onCancel = async (pendingId: number) => {
    const ok = await cancelPendingSend(pendingId)
    if (ok) {
      setzePhase(pendingId, "cancelled")
      const t = toasts.find((x) => x.pendingId === pendingId)
      try { t?.onCancelled?.() } catch { /* no-op */ }
    } else {
      // Zu spaet — der Worker hat ihn schon. Endstatus abwarten.
      setzePhase(pendingId, "checking")
    }
  }

  if (toasts.length === 0) return null

  return (
    <div
      data-testid="versandhinweise"
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        pointerEvents: "none",
        ...stil,
      }}
    >
      {toasts.map((t) => {
        const farben = FARBEN[t.phase]
        const hinweis = t.phase === "failed" ? explainSendError(t.error) : null
        return (
          <div
            key={t.pendingId}
            onPointerDown={griff.onPointerDown}
            style={{
              ...griff.style,
              pointerEvents: "auto",
              minWidth: 320,
              maxWidth: 440,
              padding: "12px 16px",
              background: farben.bg,
              color: farben.fg,
              border: `1px solid ${farben.border}`,
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              fontSize: 14,
            }}
            role={t.phase === "failed" ? "alert" : undefined}
          >
            {t.phase === "failed" ? (
              <AlertTriangle className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
            ) : t.phase === "sent" ? (
              <Check className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
            ) : (
              <Send className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{titel(t)}</div>
              <div
                style={{
                  fontSize: 12, opacity: 0.85,
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: t.phase === "failed" ? "normal" : "nowrap",
                }}
              >
                {t.label}
              </div>
              {hinweis && (
                <div style={{ fontSize: 12, marginTop: 6 }}>{hinweis}</div>
              )}
              {t.phase === "failed" && t.error && (
                <div
                  style={{
                    fontSize: 12, marginTop: 6, opacity: 0.8,
                    fontFamily: "ui-monospace, monospace", wordBreak: "break-word",
                  }}
                >
                  {t.error}
                </div>
              )}
            </div>
            {t.phase === "counting" && (
              <button
                onClick={() => void onCancel(t.pendingId)}
                style={{
                  background: "#fff",
                  color: "#111827",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Stoppen
              </button>
            )}
            {t.phase !== "counting" && (
              <button
                onClick={() =>
                  setToasts((prev) => prev.filter((x) => x.pendingId !== t.pendingId))
                }
                style={{
                  background: "transparent",
                  color: farben.fg,
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                  flexShrink: 0,
                }}
                aria-label="Schliessen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function titel(t: ToastState): string {
  switch (t.phase) {
    case "counting": return `Sendet in ${t.remainingS}s`
    case "checking": return "Wird gesendet..."
    case "sent": return "Gesendet"
    case "cancelled": return "Send abgebrochen"
    case "failed": return "NICHT gesendet"
    case "unknown": return "Versand laeuft noch"
  }
}

const FARBEN: Record<Phase, { bg: string; fg: string; border: string }> = {
  counting: { bg: "#1f2937", fg: "#fff", border: "#0f172a" },
  checking: { bg: "#e2e8f0", fg: "#475569", border: "#cbd5e1" },
  sent: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  cancelled: { bg: "#dcfce7", fg: "#166534", border: "#86efac" },
  failed: { bg: "#fef2f2", fg: "#7f1d1d", border: "#dc2626" },
  unknown: { bg: "#e2e8f0", fg: "#475569", border: "#cbd5e1" },
}
